import { mapParcelOrgToApi } from 'dataMappers/parcelOrg.dataMapper';
import EVENT_PHASE_KEY from '@pperform/pp-constants/eventPhase.constants';
import { SHIPMENT_STATUS } from '@pperform/pp-constants/parcel';
import type { GlobalConfig } from 'interfaces/GlobalConfig.interface';
import { useEffect, useRef, useState } from 'preact/hooks';
import { agentService } from 'services/agent.services';
import {
  getIsDemoExperience,
  getShipmentParams,
} from 'services/globalConfig.services';
import { ptpCatchError } from 'services/sentry.services';
import { getShipments } from 'services/shipment.services';
import { isRuntimeError } from 'utils/fetcher.utils';
import {
  convertArrayToObject,
  toList,
  updateItem,
} from 'utils/normalizer.utils';
import { useFeatureToggleContext } from 'FeatureConfig.context';
import type Shipment from 'interfaces/PmParcel';
import { useGlobalConfigContext } from 'GlobalConfig.context';
import { useSearchParams } from 'hooks/useSearchParams';

const DEFAULT_SHIPMENTS_STATE = { entities: {}, ids: [] };

/**
 * The demo bridge announces a refilled slot with this event. The three
 * post-purchase screens share one document, so without re-reading the later
 * screens render the Confirmed data.
 */
const DEMO_PAYLOAD_EVENT = 'demo:payload';

export const useShipment = () => {
  const { searchParams } = useSearchParams();

  const globalConfig = useGlobalConfigContext();
  const featureToggles = useFeatureToggleContext();
  const [shipments, setShipments] = useState(DEFAULT_SHIPMENTS_STATE);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const isAgentInitiated = useRef(false);

  const handleUpdateShipment = (shipment) => {
    const newShipments = updateItem(shipments, shipment);
    setShipments(newShipments);
  };

  const handleReloadShipment = async (shipmentPk: number) => {
    try {
      setIsLoading(true);
      setHasError(false);
      const shipmentParams = getShipmentParams(globalConfig.config);
      const shipments = await getShipments(
        globalConfig,
        shipmentParams,
        featureToggles,
      );
      const shipment = shipments.find((s) => s.id === shipmentPk);
      handleUpdateShipment(shipment);
    } catch (error) {
      if (isRuntimeError(error)) {
        ptpCatchError(error);
      }

      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Returned by the paths that register nothing, so every branch returns a cleanup.
    let removeDemoListener = () => undefined;

    if (!globalConfig.id) {
      return removeDemoListener;
    }

    const isDemoExperience = getIsDemoExperience();
    const shipmentParams = getShipmentParams(globalConfig.config);

    // A demo carries no tracking number - the payload arrives over the bridge -
    // so this guard would return before the fetch and strand the slot unread.
    if (!isDemoExperience && !shipmentParams.length) {
      setShipments(DEFAULT_SHIPMENTS_STATE);
      return removeDemoListener;
    }

    const handleGetShipments = async () => {
      try {
        setIsLoading(true);
        setHasError(false);

        const shipments = await getShipments(
          globalConfig,
          shipmentParams,
          featureToggles,
        );
        setShipments(convertArrayToObject(shipments));
      } catch (error) {
        if (isRuntimeError(error)) {
          console.error('[PTP] can not load shipment', error);
          ptpCatchError(error);
        }

        setHasError(true);
        setShipments(DEFAULT_SHIPMENTS_STATE);
      } finally {
        setIsLoading(false);
      }
    };

    handleGetShipments();

    if (!isDemoExperience) {
      return removeDemoListener;
    }

    // Removed on the next run as well as on unmount: this effect re-runs on a
    // config change, and a listener left behind would refetch once per past run.
    window.addEventListener(DEMO_PAYLOAD_EVENT, handleGetShipments);
    removeDemoListener = () => {
      window.removeEventListener(DEMO_PAYLOAD_EVENT, handleGetShipments);
      return undefined;
    };

    return removeDemoListener;
  }, [globalConfig, featureToggles, searchParams]);

  useEffect(() => {
    // Prevent the session-init event from being sent if there are no shipments or if it has already been sent.
    if (shipments.ids.length === 0 || isAgentInitiated.current) {
      return;
    }

    isAgentInitiated.current = true;

    const shipment = shipments.entities[shipments.ids[0]];

    const shipmentLastEvent = shipment.parcel?.lastEvent?.eventTypeMasterData;
    const eventPhaseKey = shipmentLastEvent?.phaseKey;

    const parcelIsInReturnToSenderOrOthersPhase =
      eventPhaseKey === EVENT_PHASE_KEY.return_to_sender ||
      eventPhaseKey === EVENT_PHASE_KEY.others;

    const isPendingOrOpenShipment =
      shipment.shipmentStatus === SHIPMENT_STATUS.OPEN ||
      shipment.shipmentStatus === SHIPMENT_STATUS.PENDING;

    if (parcelIsInReturnToSenderOrOthersPhase || isPendingOrOpenShipment)
      return;

    agentService.init(shipment.id);
    agentService.tap('session-init', {
      browser: {
        locale: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      shipment: mapParcelOrgToApi(shipment),
      organization_id: globalConfig.organizationId,
      tracking_experience: { slug: globalConfig.cmsSlug },
    });
  }, [shipments]);

  return {
    shipments: toList(shipments) as Shipment[],
    isLoading,
    hasError,
    updateShipment: handleUpdateShipment,
    reloadShipment: handleReloadShipment,
  };
};
